import React, { useEffect, useState, useMemo } from 'react';
import { Target, Building2, TrendingUp, Loader2, ChevronDown, ChevronUp, Download, Zap, Sparkles } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { IntelligenceLayout, fetchWithAuth, MetricCard, useCompanyFilters, CompanyFilterBar, CompanyTypeBadge } from './IntelligenceLayout';
import { WatchlistButton } from '../components/WatchlistButton';
import CompanyDetailModal from '../components/CompanyDetailModal';
import { CompanyActionMenu } from '../components/CompanyActionMenu';
import { QuickOutreachModal } from '../components/pipelines/QuickOutreachModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SURGE_STYLES = {
  'New Hiring Wave': 'bg-purple-100 text-purple-700 border-purple-200',
  'Hiring Surge': 'bg-red-100 text-red-700 border-red-200',
  'Active': 'bg-amber-100 text-amber-700 border-amber-200',
  'Steady': 'bg-gray-100 text-gray-500 border-gray-200',
};

const WeeklyTargets = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('target_score');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [outreachTarget, setOutreachTarget] = useState(null);
  const { excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, filterParams } = useCompanyFilters();

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/weekly-targets?limit=50${filterParams}`)
      .then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, [excludeStaffing, excludeRetail]);

  const handleSort = (f) => {
    if (sortField === f) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(f); setSortDir('desc'); }
  };

  const companies = data?.companies || [];
  const sorted = useMemo(() => [...companies].sort((a, b) => {
    const m = sortDir === 'desc' ? -1 : 1;
    if (sortField === 'company') return m * a.company.localeCompare(b.company);
    return m * ((a[sortField] || 0) - (b[sortField] || 0));
  }), [companies, sortField, sortDir]);

  const surgeCount = companies.filter(c => ['New Hiring Wave', 'Hiring Surge'].includes(c.surge_status)).length;
  const chartData = companies.slice(0, 15).map(c => ({
    company: c.company.length > 16 ? c.company.slice(0, 14) + '..' : c.company,
    target: c.target_score,
    pressure: c.pressure_score,
  }));

  const downloadCSV = () => {
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/api/intelligence/weekly-targets/csv?limit=50&token=${token}`, '_blank');
  };

  const downloadCSVFetch = async () => {
    try {
      const response = await fetch(`${API_URL}/api/intelligence/weekly-targets/csv?limit=50`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `weekly_targets_${data?.week_of || 'export'}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const SortHeader = ({ field, label, align = 'left' }) => (
    <th className={`text-${align} px-3 py-3 cursor-pointer hover:text-[#0A1F44] select-none whitespace-nowrap text-xs`}
      onClick={() => handleSort(field)}>
      <span className="inline-flex items-center gap-1">{label}
        {sortField === field && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
      </span>
    </th>
  );

  return (
    <IntelligenceLayout title="Weekly Targets" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-indigo-50 rounded-xl flex items-center justify-center">
              <Target className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="weekly-targets-title">Weekly Target Companies</h1>
              <p className="text-sm text-gray-500">Prioritized outreach list — week of {data?.week_of || '...'}</p>
            </div>
          </div>
          <button onClick={downloadCSVFetch} data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-semibold hover:bg-[#0d2a5c] transition-colors">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </header>

      <main className="p-8 space-y-6">
        <CompanyFilterBar excludeStaffing={excludeStaffing} setExcludeStaffing={setExcludeStaffing}
          excludeRetail={excludeRetail} setExcludeRetail={setExcludeRetail} />
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Generating target list...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="targets-metrics">
              <MetricCard label="Target Companies" value={data?.total_targets || 0} icon={Target} />
              <MetricCard label="Surge Signals" value={surgeCount} icon={Zap} subtext="Active surges" />
              <MetricCard label="Avg Target Score" value={companies.length ? Math.round(companies.reduce((s, c) => s + c.target_score, 0) / companies.length) : 0} icon={TrendingUp} />
              <MetricCard label="Avg Pressure" value={companies.length ? Math.round(companies.reduce((s, c) => s + c.pressure_score, 0) / companies.length) : 0} icon={Sparkles} />
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="targets-chart">
              <h3 className="text-sm font-semibold text-[#0A1F44] mb-4">Target Score vs Hiring Pressure</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="company" width={120} tick={{ fontSize: 9 }} />
                  <Tooltip />
                  <Bar dataKey="pressure" fill="#94a3b8" name="Pressure" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="target" fill="#4f46e5" name="Target Score" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="targets-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50/30">
                      <th className="text-left px-3 py-3 w-8">#</th>
                      <SortHeader field="company" label="Company" />
                      <SortHeader field="target_score" label="Target Score" align="center" />
                      <SortHeader field="pressure_score" label="Pressure" align="right" />
                      <th className="text-center px-3 py-3">Surge</th>
                      <SortHeader field="future_probability" label="Future Prob" align="right" />
                      <SortHeader field="total_jobs_30d" label="Jobs (30d)" align="right" />
                      <SortHeader field="jobs_7d" label="Jobs (7d)" align="right" />
                      <SortHeader field="cities_hiring" label="Cities" align="right" />
                      <SortHeader field="states_hiring" label="States" align="right" />
                      <th className="text-left px-3 py-3">Industry</th>
                      <th className="text-center px-3 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((item, i) => (
                      <tr key={item.company} className={`border-t border-gray-100 transition-colors ${i < 5 ? 'bg-indigo-50/30' : 'hover:bg-[#f8f6f0]'}`}
                        data-testid={`target-row-${i}`}>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${i < 3 ? 'bg-indigo-600 text-white' : i < 10 ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</span>
                        </td>
                        <td className="px-3 py-3"><div className="flex items-center gap-2"><WatchlistButton company={item.company} sourceModule="weekly-targets" /><span className="font-semibold text-[#0A1F44]">{item.company}</span></div></td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <div className="w-12 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${item.target_score}%` }} />
                            </div>
                            <span className="text-xs font-bold text-indigo-700">{item.target_score}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right text-gray-600">{item.pressure_score}</td>
                        <td className="px-3 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-[10px] font-bold border ${SURGE_STYLES[item.surge_status] || SURGE_STYLES['Steady']}`}>{item.surge_status}</span>
                        </td>
                        <td className="px-3 py-3 text-right text-gray-600">{item.future_probability}</td>
                        <td className="px-3 py-3 text-right font-medium">{item.total_jobs_30d}</td>
                        <td className="px-3 py-3 text-right text-gray-600">{item.jobs_7d}</td>
                        <td className="px-3 py-3 text-right text-gray-600">{item.cities_hiring}</td>
                        <td className="px-3 py-3 text-right text-gray-600">{item.states_hiring}</td>
                        <td className="px-3 py-3">{item.industry ? <div className="flex flex-col gap-1"><span className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200 w-fit">{item.industry}</span><CompanyTypeBadge type={item.company_type} /></div> : '—'}</td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => setSelectedCompany(item.company)}
                              className="px-3 py-1 text-[10px] font-semibold bg-[#D4AF37] text-[#0A1F44] rounded-full hover:bg-[#D4AF37]/80 transition-all"
                              data-testid={`track-btn-${i}`}>
                              Track
                            </button>
                            <CompanyActionMenu
                              companyName={item.company}
                              companyData={{ industry: item.industry }}
                              onOutreach={(c) => setOutreachTarget(c)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
      {selectedCompany && <CompanyDetailModal company={selectedCompany} onClose={() => setSelectedCompany(null)} />}
      {outreachTarget && <QuickOutreachModal company={outreachTarget} onClose={() => setOutreachTarget(null)} onSent={() => setOutreachTarget(null)} />}
    </IntelligenceLayout>
  );
};

export default WeeklyTargets;
